// PounceAppointmentDetail.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Ek appointment ki poori detail (/pounce-appointments/:id) — list ke
// "View details" se khulta hai. List appointment ko router state me bhej deti
// hai; seedha URL / refresh pe id se dobara mangwate hain.
//
// Har section tabhi banta hai jab uska data ho (qualification, activity…).
//
// ⚠️ Reschedule / Cancel ka endpoint abhi nahi hai — dekho `notify`.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./PounceAppointmentDetail.module.css";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import { fetchPounceAppointment } from "./staticAppointments";
import { formatDay, formatStamp, formatTimeRange, getBadge, initials } from "./appointmentHelpers";
import {
  BackIcon,
  BADGE_ICONS,
  BuildingIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeIcon,
  MailIcon,
  PhoneIcon,
  SourceIcon,
  SparkIcon,
  TYPE_ICONS,
} from "./AppointmentIcons";

const LIST_PATH = "/pounce-appointments";

const PounceAppointmentDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const passed = location.state?.appointment;

  // undefined = load ho raha hai, null = mila nahi.
  const [appt, setAppt] = useState(passed && String(passed.id) === String(id) ? passed : undefined);
  const [forbidden, setForbidden] = useState(false);

  const agentId =
    sessionStorage.getItem("SelectAgentId") || localStorage.getItem("SelectAgentId") || "";

  useEffect(() => {
    if (appt !== undefined) return undefined;
    let cancelled = false;
    fetchPounceAppointment(id, agentId)
      .then((row) => !cancelled && setAppt(row))
      .catch((err) => {
        if (cancelled) return;
        // 403 = team member ke paas appointments ki permission nahi.
        setForbidden(err?.response?.status === 403);
        setAppt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, appt, agentId]);

  // Seedha URL se aaye ho to history me list nahi hoti — tab list pe bhejo.
  const goBack = () => (location.key === "default" ? navigate(LIST_PATH, { replace: true }) : navigate(-1));

  const header = (
    <div className={styles.topBar}>
      <button type="button" className={styles.backBtn} onClick={goBack} aria-label="Back to appointments">
        <BackIcon />
      </button>
      <OutboundCallerBrand />
    </div>
  );

  if (!appt) {
    return (
      <div className={styles.page}>
        {header}
        {appt === undefined ? (
          <div className={`${styles.section} ${styles.skeleton}`} aria-busy="true" aria-label="Loading appointment" />
        ) : (
          <div className={styles.state}>
            <p className={styles.stateTitle}>
              {forbidden ? "You don't have access to appointments" : "We couldn't find this appointment"}
            </p>
            <button type="button" className={styles.primaryBtn} onClick={() => navigate(LIST_PATH, { replace: true })}>
              Back to appointments
            </button>
          </div>
        )}
        <PounceFooterNav agentId={agentId} active="appointments" />
      </div>
    );
  }

  const badge = getBadge(appt);
  const BadgeIcon = BADGE_ICONS[badge.icon];
  const TypeIcon = TYPE_ICONS[appt.type] || TYPE_ICONS.phone;
  const lead = appt.lead || {};
  const isUpcoming = appt.status === "upcoming";

  const leadRows = [
    { key: "phone", label: "Phone", value: lead.phone, Icon: PhoneIcon, href: lead.phone && `tel:${lead.phone}` },
    { key: "email", label: "Email", value: lead.email, Icon: MailIcon, href: lead.email && `mailto:${lead.email}` },
    { key: "company", label: "Company", value: lead.company, Icon: BuildingIcon },
    { key: "source", label: "Source", value: lead.source, Icon: SourceIcon },
  ].filter((row) => row.value);

  return (
    <div className={styles.page}>
      {header}

      <div className={styles.titleRow}>
        <h1 className={styles.title}>Appointment Details</h1>
        <span className={`${styles.badge} ${styles[badge.tone]}`}>
          <BadgeIcon size={15} />
          {badge.label}
        </span>
      </div>
      <p className={styles.subtitle}>View complete details about this appointment and lead.</p>

      {/* ── Appointment ─────────────────────────────────────────────────── */}
      <section className={styles.section}>
        <div className={styles.leadHead}>
          {lead.avatar ? (
            <img className={styles.avatar} src={lead.avatar} alt="" />
          ) : (
            <span className={styles.avatar} aria-hidden="true">
              {initials(lead.name) || "#"}
            </span>
          )}
          <span className={styles.who}>
            <span className={styles.name}>{lead.name || lead.phone}</span>
            {lead.source && <span className={styles.muted}>{lead.source}</span>}
          </span>
          {appt.status !== "cancelled" && (
            <span className={`${styles.badge} ${styles.badgeSuccess}`}>
              <CalendarIcon size={15} />
              Booked
            </span>
          )}
        </div>

        <div className={styles.meeting}>
          <span className={styles.typeIcon}>
            <TypeIcon size={20} />
          </span>
          <span className={styles.meetingTitle}>{appt.title}</span>
        </div>

        <div className={styles.facts}>
          <div className={styles.fact}>
            <span className={styles.factIcon}>
              <CalendarIcon />
            </span>
            <span className={styles.factText}>
              <span className={styles.muted}>Date</span>
              <strong>{formatDay(appt.startTime, appt.timeZone)}</strong>
            </span>
          </div>
          <div className={styles.fact}>
            <span className={styles.factIcon}>
              <ClockIcon />
            </span>
            <span className={styles.factText}>
              <span className={styles.muted}>Time</span>
              <strong>{formatTimeRange(appt.startTime, appt.durationMin, appt.timeZone)}</strong>
              <span className={styles.muted}>{appt.durationMin} min</span>
            </span>
          </div>
          {appt.timezone && (
            <div className={styles.fact}>
              <span className={styles.factIcon}>
                <GlobeIcon />
              </span>
              <span className={styles.factText}>
                <span className={styles.muted}>Timezone</span>
                <strong>{appt.timezone}</strong>
              </span>
            </div>
          )}
        </div>

        {appt.provider && (
          <div className={styles.provider}>
            <img src="/svg/google_calendar.svg" alt="" width="18" height="18" />
            {appt.provider}
          </div>
        )}

        {appt.status === "cancelled" && appt.cancelReason && (
          <div className={styles.reason}>
            <strong>Reason</strong>
            {appt.cancelReason}
          </div>
        )}

        {appt.bookedByPounce && (
          <div className={styles.aiNote}>
            <SparkIcon size={16} />
            This appointment was booked by Outbound Caller using AI.
          </div>
        )}
      </section>

      {/* ── Lead details ────────────────────────────────────────────────── */}
      {leadRows.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Lead Details</h2>
          <dl className={styles.rows}>
            {leadRows.map(({ key, label, value, Icon, href }) => (
              <div key={key} className={styles.row}>
                <dt className={styles.rowLabel}>
                  <Icon size={16} />
                  {label}
                </dt>
                <dd className={styles.rowValue}>{value}</dd>
                {href && (
                  <a className={styles.rowAction} href={href} aria-label={`${label} ${lead.name || ""}`}>
                    <Icon size={16} />
                  </a>
                )}
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* ── Qualification ───────────────────────────────────────────────── */}
      {appt.qualification && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Qualification Summary</h2>
          {appt.qualification.summary && (
            <p className={styles.summary}>
              <SparkIcon size={16} />
              <span>{appt.qualification.summary}</span>
            </p>
          )}
          {appt.qualification.items?.length > 0 && (
            <dl className={styles.rows}>
              {appt.qualification.items.map((item) => (
                <div key={item.label} className={styles.row}>
                  <dt className={styles.rowLabel}>{item.label}</dt>
                  <dd className={styles.rowValue}>{item.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      )}

      {/* ── Activity ────────────────────────────────────────────────────── */}
      {appt.activity?.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Outbound Caller Activity</h2>
          <ol className={styles.timeline}>
            {appt.activity.map((step, index) => (
              <li key={`${step.title}-${index}`} className={styles.step}>
                <span className={`${styles.dot} ${step.done ? styles.dotDone : ""}`} aria-hidden="true">
                  {step.done && <CheckCircleIcon size={18} />}
                </span>
                <span className={styles.stepText}>
                  <strong>{step.title}</strong>
                  {step.detail && <span className={styles.muted}>{step.detail}</span>}
                </span>
                {step.at && <time className={styles.stepTime}>{formatStamp(step.at)}</time>}
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ── Actions — footer ke theek upar chipke rehte hain ─────────────── */}
      <div className={styles.actions}>
        <button type="button" className={styles.primaryBtn} onClick={() => navigate("/leads")}>
          View Lead Details
        </button>
      </div>

      <PounceFooterNav agentId={agentId} active="appointments" />
    </div>
  );
};

export default PounceAppointmentDetail;
